/**
 * Sweep the chart forward a screenful at a time, saving a PNG of each window.
 *
 * <h2>The browser cannot write to `C:\stockdata\ClaudeScreenShots` on its own</h2>
 *
 * There is no API that lets a web page write to an absolute path — that would be a filesystem
 * exploit, not a feature. Two routes exist and both are here:
 *
 * <ol>
 *   <li><b>A directory handle.</b> `showDirectoryPicker()` asks the user to choose a folder once;
 *       after that the page may write into it directly, with no prompt per file. Choosing
 *       `C:\stockdata\ClaudeScreenShots` in that dialog is what puts the files there. Chromium
 *       only, and it needs a user gesture and a secure context — `localhost` counts as one.</li>
 *   <li><b>Ordinary downloads.</b> The fallback when the picker is unavailable or declined: one
 *       download per image, landing wherever the browser is configured to put downloads. The
 *       filenames are identical, so the set can be moved in one go.</li>
 * </ol>
 *
 * <p>The caller is told which route was taken, because "saved 14 images" means two different things
 * and only one of them ends with the files in the requested folder.
 *
 * <h2>Windows are planned before anything is captured</h2>
 *
 * {@link planSweep} is pure arithmetic over the date array and returns the whole list up front. The
 * capture loop then walks it. That split is what makes the tiling testable — the alternative,
 * deciding the next window from the chart's state after each render, would put the one part worth
 * checking behind a canvas and an animation frame.
 */

/** Where the images are meant to end up. Shown to the user; the picker is what actually decides. */
export const TARGET_DIRECTORY = 'C:\\stockdata\\ClaudeScreenShots';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * `Mon-YYYY` for a `yyyy-MM-dd` date.
 *
 * <p>Parsed by splitting rather than with `new Date`, which reads a bare `yyyy-MM-dd` as UTC
 * midnight and then renders it in local time — west of Greenwich that is the previous day, so a
 * window starting 1 March would be filed under February.
 */
export function monthYear(date) {
  const m = /^(\d{4})-(\d{2})/.exec(String(date || ''));
  if (!m) return null;
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${month}-${m[1]}` : null;
}

/** `from_Mon-YYYY-to_Mon-YYYY.png`, as the study asks for it. */
export function sweepFileName(fromDate, toDate, { extension = 'png' } = {}) {
  const from = monthYear(fromDate);
  const to = monthYear(toDate);
  if (!from || !to) return null;
  return `from_${from}-to_${to}.${extension}`;
}

/**
 * Every window the sweep will capture, left to right, as `{start, end}` bar indices.
 *
 * <p>Each window starts on the bar immediately after the previous one ended, so the images tile the
 * history without overlapping and without a gap. The last window is short rather than reaching past
 * the data — padding it would file bars under a range that does not exist.
 *
 * @param {string[]} dates every bar's date, in order
 * @param {number} width how many bars a window holds — the current zoom width
 * @param {number} from the first bar to include, defaulting to the first bar
 * @param {number} to the last bar to include, defaulting to the last real bar
 */
export function planSweep(dates, width, { from = 0, to = null } = {}) {
  const list = Array.isArray(dates) ? dates : [];
  const span = Math.floor(Number(width));
  const last = Number.isInteger(to) ? Math.min(to, list.length - 1) : list.length - 1;
  const first = Math.max(0, Math.floor(Number(from) || 0));
  if (!list.length || !Number.isFinite(span) || span < 1 || first > last) return [];

  const windows = [];
  for (let start = first; start <= last; start += span) {
    const end = Math.min(start + span - 1, last);
    windows.push({
      start,
      end,
      fromDate: list[start],
      toDate: list[end],
      name: sweepFileName(list[start], list[end]),
    });
  }
  return windows;
}

/* ─────────────────────────────────────────── saving ─────────────────────────────────────────── */

/** Is the directory route available in this browser? */
export function canPickDirectory() {
  return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
}

/**
 * A place to put the images: a chosen directory, or the browser's downloads.
 *
 * <p>Returns `{ mode, write(name, blob), label }`. The caller reports `label` rather than composing
 * its own message, so what the user is told always matches where the bytes actually went.
 *
 * <p>A declined picker falls back rather than aborting: the sweep is worth having in Downloads, and
 * cancelling a folder dialog is not a request to cancel the export.
 */
export async function openSweepTarget({ picker = null } = {}) {
  const pick = picker || (canPickDirectory() ? window.showDirectoryPicker : null);
  if (pick) {
    try {
      const handle = await pick.call(window, { mode: 'readwrite', id: 'astrocharts-screenshots' });
      return {
        mode: 'directory',
        label: `the folder you chose (${handle.name})`,
        async write(name, blob) {
          const file = await handle.getFileHandle(name, { create: true });
          const stream = await file.createWritable();
          await stream.write(blob);
          await stream.close();
        },
      };
    } catch (err) {
      // AbortError is the user closing the dialog. Anything else — a permission refusal, a folder
      // the page may not have — lands here too, and the answer is the same: fall back.
      if (err && err.name !== 'AbortError') console.warn('[sweep] directory picker failed', err);
    }
  }
  return {
    mode: 'download',
    label: 'your browser\'s Downloads folder',
    async write(name, blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoked on the next tick, not immediately: Chrome cancels a download whose blob URL is
      // released before it has been read, and a sweep issues these back to back.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    },
  };
}

/** A `data:` URL as a Blob, which is what both save routes take. */
export function dataUrlToBlob(dataUrl) {
  const [head, body] = String(dataUrl || '').split(',');
  if (!body || !/^data:/.test(head)) return null;
  const mime = (/data:([^;]+)/.exec(head) || [])[1] || 'image/png';
  if (!/;base64$/.test(head)) return new Blob([decodeURIComponent(body)], { type: mime });
  const binary = atob(body);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/**
 * Run the sweep: for each window, move the chart, let it paint, capture, save.
 *
 * <p>The wait between moving and capturing is not optional and not a guess at a duration. ECharts
 * paints on an animation frame, so a `getDataURL` issued in the same tick as the zoom returns the
 * PREVIOUS window — every image would be one frame stale, which on a tiling sweep means every image
 * shows the wrong months under the right filename. `settle` waits two frames and, where the
 * environment has it, a following timeout, which is what the chart's own animation needs.
 *
 * <p>The original zoom is restored at the end, including when a capture throws. A tool that leaves
 * the chart somewhere else than it found it is one the user has to undo by hand.
 *
 * @returns {Promise<{saved: number, failed: number, mode: string, label: string, files: string[]}>}
 */
export async function runSweep({ chart, dates, windows, target, settle, onProgress }) {
  const wait = settle || (() => new Promise((resolve) => {
    const raf = typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame : (cb => setTimeout(cb, 16));
    raf(() => raf(() => setTimeout(resolve, 60)));
  }));

  const before = readZoomWindow(chart, dates);
  const files = [];
  let failed = 0;

  try {
    for (let i = 0; i < windows.length; i++) {
      const w = windows[i];
      if (onProgress) onProgress(i, windows.length, w);
      chart.dispatchAction({ type: 'dataZoom', dataZoomIndex: 0, startValue: w.start, endValue: w.end });
      chart.dispatchAction({ type: 'dataZoom', dataZoomIndex: 1, startValue: w.start, endValue: w.end });
      // eslint-disable-next-line no-await-in-loop -- each capture needs the previous one painted
      await wait();
      try {
        const url = chart.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#ffffff' });
        const blob = dataUrlToBlob(url);
        if (!blob || !w.name) { failed++; continue; }
        // eslint-disable-next-line no-await-in-loop -- writes are sequential by design
        await target.write(w.name, blob);
        files.push(w.name);
      } catch (err) {
        console.warn('[sweep] capture failed', w, err);
        failed++;
      }
    }
  } finally {
    if (before) {
      chart.dispatchAction({ type: 'dataZoom', dataZoomIndex: 0, ...before });
      chart.dispatchAction({ type: 'dataZoom', dataZoomIndex: 1, ...before });
    }
  }

  return { saved: files.length, failed, mode: target.mode, label: target.label, files };
}

/**
 * The chart's current window as `{startValue, endValue}` bar indices.
 *
 * <p>ECharts reports the zoom as percentages as well as values, and the percentages are what a
 * `getOption()` read gives back after the user has dragged. Converting from those rather than from
 * `startValue` is what keeps the sweep width equal to what is actually on screen.
 */
export function readZoomWindow(chart, dates) {
  const total = (Array.isArray(dates) ? dates.length : 0);
  if (!chart || !total) return null;
  let zoom;
  try {
    zoom = (chart.getOption()?.dataZoom || []).find(z => z && (z.xAxisIndex || z.orient !== 'vertical'));
  } catch (err) {
    return null;
  }
  if (!zoom) return null;

  const fromValue = v => (Number.isFinite(v) ? Math.round(v) : null);
  const fromPercent = p => (Number.isFinite(p) ? Math.round((p / 100) * (total - 1)) : null);
  const start = fromValue(zoom.startValue) ?? fromPercent(zoom.start) ?? 0;
  const end = fromValue(zoom.endValue) ?? fromPercent(zoom.end) ?? total - 1;
  const lo = Math.max(0, Math.min(start, end));
  const hi = Math.min(total - 1, Math.max(start, end));
  return hi > lo ? { startValue: lo, endValue: hi } : null;
}
